import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { teamAPI, graphicAPI } from '../services/api';
import { useGraphicForm } from '../hooks/useGraphicForm';
import { useBackgroundRemoval } from '../hooks/useBackgroundRemoval';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FormHeader from '../components/create-graphic/FormHeader';
import {
  GraphicsLimitReached,
  LoadingState,
  NoTeamsState,
} from '../components/create-graphic/EmptyStates';
import TeamSelector from '../components/create-graphic/TeamSelector';
import TemplateTypeSelector from '../components/create-graphic/TemplateTypeSelector';
import BackgroundStyleSelector from '../components/create-graphic/BackgroundStyleSelector';
import TemplateDesignSelector from '../components/create-graphic/TemplateDesignSelector';
import PhotoUploadSection from '../components/create-graphic/PhotoUploadSection';
import GamePreviewRecapSharedFields from '../components/create-graphic/GamePreviewRecapSharedFields';
import GamePreviewFields from '../components/create-graphic/GamePreviewFields';
import GameRecapFields from '../components/create-graphic/GameRecapFields';
import TryoutFields from '../components/create-graphic/TryoutFields';
import HighlightFields from '../components/create-graphic/HighlightFields';
import FormActions from '../components/create-graphic/FormActions';
import GraphicPreviewRenderer from '../components/create-graphic/GraphicPreviewRenderer';
import TeamSwitchModal from '../components/create-graphic/TeamSwitchModal';
import LayoutCustomizationModal from '../components/create-graphic/LayoutCustomizationModal';
import DateFormatModal from '../components/create-graphic/DateFormatModal';
import SavingOverlay from '../components/create-graphic/SavingOverlay';
import ToastNotifications from '../components/create-graphic/ToastNotifications';
import './CreateGraphic.css';

function CreateGraphic() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [canCreateGraphic, setCanCreateGraphic] = useState(true);
  const previewContainerRef = useRef(null);

  // Use custom hook for all form logic
  const {
    teams,
    setTeams,
    downloading,
    saving,
    templateType,
    template,
    setTemplate,
    setCurrentGraphicId,
    saveMessage,
    savingOverlay,
    backgroundStyle,
    graphicsLimitInfo,
    setGraphicsLimitInfo,
    homeTeamLogo,
    setHomeTeamLogo,
    awayTeamLogoPreview,
    setAwayTeamLogoPreview,
    backgroundPhotoPreview,
    setBackgroundPhotoPreview,
    successMessage,
    toastMessage,
    showRosterDropdown,
    setShowRosterDropdown,
    filteredRoster,
    playerPhotoPreview,
    originalPlayerPhoto,
    setOriginalPlayerPhoto,
    cutoutPlayerPhoto,
    activePlayerPhotoVersion,
    setCutoutPlayerPhoto,
    setActivePlayerPhotoVersion,
    backgroundCropping,
    backgroundTempImage,
    playerCropping,
    playerTempImage,
    formData,
    setFormData,
    teamColors,
    setTeamColors,
    teamSport,
    setTeamSport,
    setIsOrgTeam,
    isOrgTeam,
    setOrgTemplateSettings,
    orgTemplateSettings,
    showSwitchWarning,
    pendingTeamSwitch,
    showLayoutModal,
    setShowLayoutModal,
    layoutSettings,
    setLayoutSettings,
    handleLayoutChange,
    showDateFormatModal,
    setShowDateFormatModal,
    dateFormat,
    setDateFormat,
    fetchRosterAsync,
    handleTeamChange,
    handleConfirmSwitch,
    handleCancelSwitch,
    handlePlayerPhotoChange,
    handleRemovePlayerPhoto,
    handleChange,
    handleAdditionalInfoChange,
    handleHighlightStatsChange,
    handleTemplateChange,
    handleSave,
    handleSaveAndCreateAnother,
    handleBackgroundStyleChange,
    handleAwayTeamLogoChange,
    handleRemoveAwayTeamLogo,
    handleBackgroundPhotoChange,
    handleRemoveBackgroundPhoto,
    handleBackgroundCropComplete,
    handleBackgroundCropCancel,
    handlePlayerCropComplete,
    handlePlayerCropCancel,
    handleEditBackgroundCrop,
    handleSetBackgroundPhotoFromUrl,
    handleEditPlayerCrop,
    dominantImagePreview,
    originalDominantImage,
    setOriginalDominantImage,
    cutoutDominantImage,
    setCutoutDominantImage,
    activeDominantImageVersion,
    setActiveDominantImageVersion,
    dominantImageCropping,
    dominantImageTempImage,
    handleDominantImageChange,
    handleRemoveDominantImage,
    handleDominantImageCropComplete,
    handleDominantImageCropCancel,
    handleEditDominantImageCrop,
    handlePlayerSearch,
    handlePlayerSelect,
    formatPlayerDisplay,
    handleDownload,
  } = useGraphicForm();

  const { removeBg, isRemoving, bgRemovalError } = useBackgroundRemoval();

  const canUseCutouts =
    isOrgTeam ||
    ['pro', 'premium', 'organization'].includes(
      graphicsLimitInfo?.subscriptionPlan
    );

  const handleRemoveBg = async () => {
    if (!originalPlayerPhoto) return;
    const result = await removeBg(originalPlayerPhoto);
    if (result) {
      const { blobUrl, toBase64 } = result;
      // Instant display — blob URL, never swapped out
      setCutoutPlayerPhoto(blobUrl);
      setActivePlayerPhotoVersion('cutout');
      setFormData((prev) => ({
        ...prev,
        playerPhoto: blobUrl,
        playerPhotoVersion: 'cutout',
      }));
      // Background conversion — only for persistence on save/reload, never touches display state
      toBase64.then((base64) => {
        setFormData((prev) => ({ ...prev, playerPhotoCutout: base64 }));
      });
    }
  };

  const handleTogglePhotoVersion = (version) => {
    setActivePlayerPhotoVersion(version);
    const photo = version === 'cutout' ? cutoutPlayerPhoto : originalPlayerPhoto;
    setFormData((prev) => ({ ...prev, playerPhoto: photo, playerPhotoVersion: version }));
  };

  const handleRemoveDominantBg = async () => {
    if (!originalDominantImage) return;
    const result = await removeBg(originalDominantImage);
    if (result) {
      const { blobUrl, toBase64 } = result;
      setCutoutDominantImage(blobUrl);
      setActiveDominantImageVersion('cutout');
      setFormData((prev) => ({
        ...prev,
        dominantImage: blobUrl,
        dominantImageVersion: 'cutout',
      }));
      toBase64.then((base64) => {
        setFormData((prev) => ({ ...prev, dominantImageCutout: base64 }));
      });
    }
  };

  const handleToggleDominantImageVersion = (version) => {
    setActiveDominantImageVersion(version);
    const photo = version === 'cutout' ? cutoutDominantImage : originalDominantImage;
    setFormData((prev) => ({ ...prev, dominantImage: photo, dominantImageVersion: version }));
  };

  // Load teams and optionally load saved graphic
  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchData = async () => {
      try {
        const [
          teamsResponse,
          graphicsLimitResponse,
          teamLimitResponse,
          allGraphicsResponse,
        ] = await Promise.all([
          teamAPI.getAll(),
          graphicAPI.getLimitInfo(),
          teamAPI.getLimitInfo(),
          graphicAPI.getAll(),
        ]);

        const limitInfo = graphicsLimitResponse.data;
        setGraphicsLimitInfo(limitInfo);

        const urlTemplateType = searchParams.get('templateType');
        if (urlTemplateType && !searchParams.get('id')) {
          handleTemplateChange(urlTemplateType);
        }

        const urlHighlightPlayer = searchParams.get('highlightPlayer');
        const urlHighlightStats = searchParams.get('highlightStats');
        if ((urlHighlightPlayer || urlHighlightStats) && !searchParams.get('id')) {
          setFormData((prev) => ({
            ...prev,
            ...(urlHighlightPlayer && { highlightPlayer: urlHighlightPlayer }),
            ...(urlHighlightStats && { highlightStats: urlHighlightStats }),
          }));
        }

        const allTeams = teamsResponse.data.filter((t) => t.deletedAt === null);
        const orgTeams = allTeams.filter((t) => t.organizationTeam === true);
        const personalTeams = allTeams.filter((t) => !t.organizationTeam);

        const limit = teamLimitResponse.data.teamLimit || Infinity;
        const sortedPersonal = [...personalTeams].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        const accessiblePersonal = sortedPersonal.slice(0, limit);
        const accessibleTeams = [...orgTeams, ...accessiblePersonal];

        setTeams(accessibleTeams);

        const allGraphics = allGraphicsResponse.data;
        const accessiblePersonalTeamIds = accessiblePersonal.map((t) => t.id);
        const personalGraphicsCount = allGraphics.filter(
          (g) =>
            g.Team &&
            g.Team.organizationTeam === false &&
            accessiblePersonalTeamIds.includes(g.teamId)
        ).length;
        const graphicsLimit = limitInfo.graphicsLimit;
        const isUnlimited = limitInfo.unlimited;

        const graphicId = searchParams.get('id');
        if (graphicId) {
          setCanCreateGraphic(true);
          const graphicResponse = await graphicAPI.getById(graphicId);
          const savedGraphic = graphicResponse.data;

          setCurrentGraphicId(savedGraphic.id);
          handleTemplateChange(savedGraphic.templateType);
          setTemplate(savedGraphic.template || 'classic');
          setFormData(savedGraphic.graphicData);

          if (savedGraphic.graphicData.backgroundPhoto) {
            setBackgroundPhotoPreview(savedGraphic.graphicData.backgroundPhoto);
          }
          if (savedGraphic.graphicData.awayTeamLogo) {
            setAwayTeamLogoPreview(savedGraphic.graphicData.awayTeamLogo);
          }
          if (savedGraphic.graphicData.playerPhotoOriginal) {
            setOriginalPlayerPhoto(savedGraphic.graphicData.playerPhotoOriginal);
          }
          if (savedGraphic.graphicData.playerPhotoCutout) {
            setCutoutPlayerPhoto(savedGraphic.graphicData.playerPhotoCutout);
          }
          if (savedGraphic.graphicData.playerPhotoVersion) {
            setActivePlayerPhotoVersion(savedGraphic.graphicData.playerPhotoVersion);
          }
          if (savedGraphic.graphicData.dominantImageOriginal) {
            setOriginalDominantImage(savedGraphic.graphicData.dominantImageOriginal);
          }
          if (savedGraphic.graphicData.dominantImageCutout) {
            setCutoutDominantImage(savedGraphic.graphicData.dominantImageCutout);
          }
          if (savedGraphic.graphicData.dominantImageVersion) {
            setActiveDominantImageVersion(savedGraphic.graphicData.dominantImageVersion);
          }
          if (savedGraphic.graphicData.layoutSettings) {
            setLayoutSettings(savedGraphic.graphicData.layoutSettings);
          }
          if (savedGraphic.graphicData.dateFormat) {
            setDateFormat(savedGraphic.graphicData.dateFormat);
          }

          const team = accessibleTeams.find(
            (t) => t.id === savedGraphic.teamId
          );
          if (team) {
            setTeamColors({
              primary: team.primaryColor || team.orgPrimaryColor || '#003366',
              secondary:
                team.secondaryColor || team.orgSecondaryColor || '#FFFFFF',
            });
            setHomeTeamLogo(team.logoUrl || team.orgLogoUrl);
            setIsOrgTeam(team.organizationTeam === true);
            setTeamSport(team.sport);
            setOrgTemplateSettings(team.orgTemplateSettings || null);
            fetchRosterAsync(team.id);
          }
        } else {
          const preSelectedTeamId = searchParams.get('teamId');
          const preSelectedTeam = preSelectedTeamId
            ? accessibleTeams.find((t) => t.id === preSelectedTeamId)
            : null;

          if (preSelectedTeam?.organizationTeam) {
            setCanCreateGraphic(true);
          } else if (!isUnlimited && personalGraphicsCount >= graphicsLimit) {
            setCanCreateGraphic(false);
          } else {
            setCanCreateGraphic(true);
          }

          if (preSelectedTeamId && accessibleTeams.length > 0) {
            try {
              const teamResponse = await teamAPI.getById(preSelectedTeamId);
              const selectedTeam = teamResponse.data;

              setFormData((prev) => ({
                ...prev,
                selectedTeamId: selectedTeam.id,
                homeTeam: selectedTeam.name,
              }));
              setTeamColors({
                primary:
                  selectedTeam.primaryColor ||
                  selectedTeam.orgPrimaryColor ||
                  '#003366',
                secondary:
                  selectedTeam.secondaryColor ||
                  selectedTeam.orgSecondaryColor ||
                  '#FFFFFF',
              });
              setHomeTeamLogo(selectedTeam.logoUrl || selectedTeam.orgLogoUrl);
              setIsOrgTeam(selectedTeam.organizationTeam === true);
              setOrgTemplateSettings(selectedTeam.orgTemplateSettings || null);
              setTeamSport(selectedTeam.sport);
              fetchRosterAsync(selectedTeam.id);
            } catch (error) {
              console.error('Error fetching pre-selected team:', error);
            }
          } else if (accessibleTeams.length === 1 && !graphicId) {
            const onlyTeam = accessibleTeams[0];
            try {
              const teamResponse = await teamAPI.getById(onlyTeam.id);
              const fullTeamData = teamResponse.data;

              setFormData((prev) => ({
                ...prev,
                selectedTeamId: fullTeamData.id,
                homeTeam: fullTeamData.name,
              }));
              setTeamColors({
                primary:
                  fullTeamData.primaryColor ||
                  fullTeamData.orgPrimaryColor ||
                  '#003366',
                secondary:
                  fullTeamData.secondaryColor ||
                  fullTeamData.orgSecondaryColor ||
                  '#FFFFFF',
              });
              setHomeTeamLogo(fullTeamData.logoUrl || fullTeamData.orgLogoUrl);
              setIsOrgTeam(fullTeamData.organizationTeam === true);
              setOrgTemplateSettings(fullTeamData.orgTemplateSettings || null);
              fetchRosterAsync(fullTeamData.id);
            } catch (error) {
              console.error('Error fetching auto-selected team:', error);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Only re-run when URL params change

  // Auto-scroll to selected template type
  useEffect(() => {
    if (templateType && !loading) {
      setTimeout(() => {
        const activeButton = document.querySelector(
          '.template-buttons .template-btn.active'
        );
        const carousel = document.querySelector('.template-buttons');

        if (activeButton && carousel) {
          const buttonLeft = activeButton.offsetLeft;
          const buttonWidth = activeButton.offsetWidth;
          const carouselWidth = carousel.offsetWidth;
          const scrollPosition =
            buttonLeft - carouselWidth / 2 + buttonWidth / 2;

          carousel.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
      }, 500);
    }
  }, [templateType, loading]);

  // Empty states
  if (!canCreateGraphic && !formData.selectedTeamId) {
    return (
      <GraphicsLimitReached
        graphicsLimitInfo={graphicsLimitInfo}
        selectedTeamId={formData.selectedTeamId}
      />
    );
  }

  if (loading) {
    return <LoadingState />;
  }

  if (teams.length === 0) {
    return <NoTeamsState teamSport={teamSport} />;
  }

  return (
    <>
      <Navbar />

      <div className="create-graphic-container">
        <div className="graphic-builder">
          {/* Left Side - Form */}
          <div className="builder-form">
            <FormHeader selectedTeamId={formData.selectedTeamId} />

            <TemplateTypeSelector
              templateType={templateType}
              isOrgTeam={isOrgTeam}
              graphicsLimitInfo={graphicsLimitInfo}
              orgTemplateSettings={orgTemplateSettings}
              onTemplateChange={handleTemplateChange}
            />

            <form className="graphic-form">
              <TeamSelector
                teams={teams}
                selectedTeamId={formData.selectedTeamId}
                onChange={(teamId) =>
                  handleTeamChange({ target: { value: teamId } })
                }
              />


              {(templateType === 'preview' || templateType === 'recap') && (
                <GamePreviewRecapSharedFields
                  formData={formData}
                  templateType={templateType}
                  template={template}
                  graphicsLimitInfo={graphicsLimitInfo}
                  isOrgTeam={isOrgTeam}
                  awayTeamLogoPreview={awayTeamLogoPreview}
                  saving={saving}
                  downloading={downloading}
                  onChange={handleChange}
                  onAwayLogoChange={handleAwayTeamLogoChange}
                  onRemoveAwayLogo={handleRemoveAwayTeamLogo}
                  onSetAwayLogoUrl={setAwayTeamLogoPreview}
                  onDateFormatClick={() => setShowDateFormatModal(true)}
                  hideGameDate={templateType === 'recap'}
                />
              )}
              <TemplateDesignSelector
                template={template}
                templateType={templateType}
                teamSport={teamSport}
                isOrgTeam={isOrgTeam}
                graphicsLimitInfo={graphicsLimitInfo}
                orgTemplateSettings={orgTemplateSettings}
                onTemplateChange={setTemplate}
                showToast={(type, text) => {}}
              />

              {templateType === 'preview' && (
                <GamePreviewFields
                  formData={formData}
                  template={template}
                  onChange={handleChange}
                  teamColors={teamColors}
                  dominantImageCropping={dominantImageCropping}
                  dominantImageTempImage={dominantImageTempImage}
                  dominantImagePreview={dominantImagePreview}
                  saving={saving}
                  downloading={downloading}
                  onDominantImageChange={handleDominantImageChange}
                  onDominantImageCropComplete={handleDominantImageCropComplete}
                  onDominantImageCropCancel={handleDominantImageCropCancel}
                  onEditDominantImageCrop={handleEditDominantImageCrop}
                  onRemoveDominantImage={handleRemoveDominantImage}
                  canUseCutouts={canUseCutouts}
                  onRemoveDominantBg={handleRemoveDominantBg}
                  isRemoving={isRemoving}
                  bgRemovalError={bgRemovalError}
                  originalDominantImage={originalDominantImage}
                  cutoutDominantImage={cutoutDominantImage}
                  activeDominantImageVersion={activeDominantImageVersion}
                  onToggleDominantImageVersion={handleToggleDominantImageVersion}
                  backgroundCropping={backgroundCropping}
                  backgroundTempImage={backgroundTempImage}
                  backgroundPhotoPreview={backgroundPhotoPreview}
                  onBackgroundPhotoChange={handleBackgroundPhotoChange}
                  onBackgroundCropComplete={handleBackgroundCropComplete}
                  onBackgroundCropCancel={handleBackgroundCropCancel}
                  onEditBackgroundCrop={handleEditBackgroundCrop}
                  onRemoveBackgroundPhoto={handleRemoveBackgroundPhoto}
                  onSetBackgroundPhotoFromUrl={handleSetBackgroundPhotoFromUrl}
                  backgroundStyle={backgroundStyle}
                  onBackgroundStyleChange={handleBackgroundStyleChange}
                  teamColors={teamColors}
                />
              )}

              {templateType === 'recap' && (
                <GameRecapFields
                  formData={formData}
                  onChange={handleChange}
                  template={template}
                  roster={filteredRoster}
                  showRosterDropdown={showRosterDropdown}
                  onPlayerSearch={handlePlayerSearch}
                  onPlayerSelect={handlePlayerSelect}
                  onRosterDropdownChange={setShowRosterDropdown}
                  formatPlayerDisplay={formatPlayerDisplay}
                  onDateFormatClick={() => setShowDateFormatModal(true)}
                  dominantImageCropping={dominantImageCropping}
                  dominantImageTempImage={dominantImageTempImage}
                  dominantImagePreview={dominantImagePreview}
                  saving={saving}
                  downloading={downloading}
                  onDominantImageChange={handleDominantImageChange}
                  onDominantImageCropComplete={handleDominantImageCropComplete}
                  onDominantImageCropCancel={handleDominantImageCropCancel}
                  onEditDominantImageCrop={handleEditDominantImageCrop}
                  onRemoveDominantImage={handleRemoveDominantImage}
                  canUseCutouts={canUseCutouts}
                  onRemoveDominantBg={handleRemoveDominantBg}
                  isRemoving={isRemoving}
                  bgRemovalError={bgRemovalError}
                  originalDominantImage={originalDominantImage}
                  cutoutDominantImage={cutoutDominantImage}
                  activeDominantImageVersion={activeDominantImageVersion}
                  onToggleDominantImageVersion={handleToggleDominantImageVersion}
                  backgroundCropping={backgroundCropping}
                  backgroundTempImage={backgroundTempImage}
                  backgroundPhotoPreview={backgroundPhotoPreview}
                  onBackgroundPhotoChange={handleBackgroundPhotoChange}
                  onBackgroundCropComplete={handleBackgroundCropComplete}
                  onBackgroundCropCancel={handleBackgroundCropCancel}
                  onEditBackgroundCrop={handleEditBackgroundCrop}
                  onRemoveBackgroundPhoto={handleRemoveBackgroundPhoto}
                  onSetBackgroundPhotoFromUrl={handleSetBackgroundPhotoFromUrl}
                  backgroundStyle={backgroundStyle}
                  onBackgroundStyleChange={handleBackgroundStyleChange}
                  teamColors={teamColors}
                />
              )}

              {templateType === 'tryout' && (
                <TryoutFields
                  formData={formData}
                  template={template}
                  teamColors={teamColors}
                  onChange={handleChange}
                  onAdditionalInfoChange={handleAdditionalInfoChange}
                  onDateFormatClick={() => setShowDateFormatModal(true)}
                  backgroundStyle={backgroundStyle}
                  onBackgroundStyleChange={handleBackgroundStyleChange}
                  backgroundCropping={backgroundCropping}
                  backgroundTempImage={backgroundTempImage}
                  backgroundPhotoPreview={backgroundPhotoPreview}
                  onBackgroundPhotoChange={handleBackgroundPhotoChange}
                  onBackgroundCropComplete={handleBackgroundCropComplete}
                  onBackgroundCropCancel={handleBackgroundCropCancel}
                  onEditBackgroundCrop={handleEditBackgroundCrop}
                  onRemoveBackgroundPhoto={handleRemoveBackgroundPhoto}
                  onSetBackgroundPhotoFromUrl={handleSetBackgroundPhotoFromUrl}
                  saving={saving}
                  downloading={downloading}
                />
              )}

              {templateType === 'highlight' && (
                <HighlightFields
                  formData={formData}
                  roster={filteredRoster}
                  filteredRoster={filteredRoster}
                  showRosterDropdown={showRosterDropdown}
                  saving={saving}
                  downloading={downloading}
                  onPlayerSearch={handlePlayerSearch}
                  onPlayerSelect={handlePlayerSelect}
                  onRosterDropdownChange={setShowRosterDropdown}
                  onHighlightStatsChange={handleHighlightStatsChange}
                  formatPlayerDisplay={formatPlayerDisplay}
                  canUseCutouts={canUseCutouts}
                  onChange={handleChange}
                  dominantImageCropping={dominantImageCropping}
                  dominantImageTempImage={dominantImageTempImage}
                  dominantImagePreview={dominantImagePreview}
                  onDominantImageChange={handleDominantImageChange}
                  onDominantImageCropComplete={handleDominantImageCropComplete}
                  onDominantImageCropCancel={handleDominantImageCropCancel}
                  onEditDominantImageCrop={handleEditDominantImageCrop}
                  onRemoveDominantImage={handleRemoveDominantImage}
                  originalDominantImage={originalDominantImage}
                  cutoutDominantImage={cutoutDominantImage}
                  activeDominantImageVersion={activeDominantImageVersion}
                  setActiveDominantImageVersion={setActiveDominantImageVersion}
                  onRemoveDominantBg={handleRemoveDominantBg}
                  isRemovingDominant={isRemoving}
                  dominantBgRemovalError={bgRemovalError}
                  backgroundCropping={backgroundCropping}
                  backgroundTempImage={backgroundTempImage}
                  backgroundPhotoPreview={backgroundPhotoPreview}
                  onBackgroundPhotoChange={handleBackgroundPhotoChange}
                  onBackgroundCropComplete={handleBackgroundCropComplete}
                  onBackgroundCropCancel={handleBackgroundCropCancel}
                  onEditBackground={handleEditBackgroundCrop}
                  onRemoveBackground={handleRemoveBackgroundPhoto}
                  onSetBackgroundFromUrl={handleSetBackgroundPhotoFromUrl}
                  backgroundStyle={backgroundStyle}
                  onBackgroundStyleChange={handleBackgroundStyleChange}
                  teamColors={teamColors}
                />
              )}


              <FormActions
                templateType={templateType}
                formData={formData}
                saving={saving}
                downloading={downloading}
                graphicsLimitInfo={graphicsLimitInfo}
                isOrgTeam={isOrgTeam}
                successMessage={successMessage}
                saveMessage={saveMessage}
                showBackButton={false}
                onSave={handleSave}
                onSaveAndCreateAnother={handleSaveAndCreateAnother}
                onDownload={handleDownload}
              />
            </form>
          </div>

          {/* Right Side - Live Preview */}
          <div className="builder-preview">
            <div className="preview-header">
              <h3>Live Preview</h3>
              <p>This is what your graphic will look like</p>
            </div>

            {(() => {
              const isMobile = window.innerWidth <= 768;
              const isPortraitGraphic = (templateType === 'tryout' && ['poster', 'infoCard'].includes(template)) ||
                (templateType === 'highlight' && ['showcase', 'feature'].includes(template));
              const isPortraitNative = isPortraitGraphic ? 1350 : 1080;
              // On mobile: scale from 1080 native to fit container width
              // On desktop: scale from 1080 to 540 (0.5)
              const mobileContainerWidth = window.innerWidth - 24;
              const mobileScale = isMobile ? mobileContainerWidth / 1080 : 0.5;
              const containerHeight = isMobile ? Math.round(mobileScale * isPortraitNative) : undefined;

              return (
            <div
              className="preview-container"
              ref={previewContainerRef}
              style={isMobile ? {
                height: containerHeight,
                overflow: 'hidden',
                padding: 0,
                display: 'block',
                boxShadow: 'none',
                borderRadius: 0,
              } : undefined}
            >
              <span id="showRotate"></span>

              <div
                style={
                  (templateType === 'preview' && ['classic', 'stack', 'rivalry', 'poster', 'splitScreen', 'bold', 'minimal', 'photoFrame'].includes(template)) ||
                  (templateType === 'recap' && ['classic', 'finalScore', 'victory', 'highlight', 'clean'].includes(template)) ||
                  (templateType === 'tryout' && template === 'classic') ||
                  (templateType === 'highlight' && template === 'card')
                    ? { width: isMobile ? 1080 : 540, height: isMobile ? 1080 : 540, overflow: 'hidden', position: 'relative' }
                  : isPortraitGraphic
                    ? { width: isMobile ? 1080 : 540, height: isMobile ? 1350 : 675, overflow: 'hidden', position: 'relative', borderRadius: 8 }
                    : undefined
                }
              >
                <div
                  style={
                    (templateType === 'preview' && ['classic', 'stack', 'rivalry', 'poster', 'splitScreen', 'bold', 'minimal', 'photoFrame'].includes(template)) ||
                    (templateType === 'recap' && ['classic', 'finalScore', 'victory', 'highlight', 'clean'].includes(template)) ||
                    (templateType === 'tryout' && ['classic', 'poster', 'infoCard'].includes(template)) ||
                    (templateType === 'highlight' && ['showcase', 'feature', 'card'].includes(template))
                      ? { transform: `scale(${mobileScale})`, transformOrigin: 'top left' }
                      : undefined
                  }
                >
                  <GraphicPreviewRenderer
                    templateType={templateType}
                    template={template}
                    formData={formData}
                    teamSport={teamSport}
                    teamColors={teamColors}
                    backgroundStyle={backgroundStyle}
                    homeTeamLogo={
                      formData.selectedTeamId
                        ? teams.find((t) => t.id === formData.selectedTeamId)
                            ?.logoUrl
                        : null
                    }
                    awayTeamLogoPreview={awayTeamLogoPreview}
                    isOrgTeam={isOrgTeam}
                    graphicsLimitInfo={graphicsLimitInfo}
                    dateFormat={dateFormat}
                  />
                </div>
              </div>
            </div>
            );
            })()}
          </div>
        </div>
      </div>

      <TeamSwitchModal
        isVisible={showSwitchWarning}
        pendingTeam={pendingTeamSwitch}
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
      />

      <LayoutCustomizationModal
        isOpen={showLayoutModal}
        onClose={() => setShowLayoutModal(false)}
        layoutSettings={layoutSettings}
        onLayoutChange={handleLayoutChange}
        previewComponent={
          <GraphicPreviewRenderer
            templateType={templateType}
            template={template}
            formData={formData}
            teamSport={teamSport}
            teamColors={teamColors}
            backgroundStyle={backgroundStyle}
            homeTeamLogo={homeTeamLogo}
            awayTeamLogoPreview={awayTeamLogoPreview}
            isOrgTeam={isOrgTeam}
            graphicsLimitInfo={graphicsLimitInfo}
            layoutSettings={layoutSettings}
            dateFormat={dateFormat}
          />
        }
      />

      <DateFormatModal
        isOpen={showDateFormatModal}
        onClose={() => setShowDateFormatModal(false)}
        dateFormat={dateFormat}
        onFormatChange={setDateFormat}
        selectedDate={formData.gameDate || formData.tryoutDate}
      />

      <SavingOverlay message={savingOverlay} />
      <ToastNotifications toastMessage={toastMessage} />

      <Footer />
    </>
  );
}

export default CreateGraphic;
